import {
  Link,
  RichTextEditor,
  RichTextEditorProps,
  RichTextEditorStylesNames,
} from '@mantine/tiptap';
import Placeholder from '@tiptap/extension-placeholder';
import SubScript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import stylesColors from 'common/styles/styles.colors';
import React from 'react';
import { useController, useFormContext } from 'react-hook-form';

import CustomFormControl, {
  TextLabelPlacementEnum,
} from '../custom-form-control';
import { BaseElementInputProps } from '../field-type';
import { FormContext } from '../form';
import { styles } from '../styles';

type ControlType =
  | 'default'
  | 'bulletList'
  | 'textAlignment'
  | 'heading'
  | 'link'
  | 'superscript'
  | 'subscript';

export interface RichTextFieldProps
  extends BaseElementInputProps,
    Omit<RichTextEditorProps, 'type' | 'children' | 'editor'> {
  disabled?: boolean;
  label?: string;
  type: 'editor';
  required?: boolean;
  noMargin?: boolean;
  labelPlacement?: TextLabelPlacementEnum;
  controls: ControlType[];
  hideLabel?: boolean;
  overrides?: {
    [key in RichTextEditorStylesNames]?: React.CSSProperties;
  };
}

export default function RichTextInput(props: RichTextFieldProps) {
  const {
    label,
    name,
    disabled,
    controls,
    overrides,
    placeholder,
    ...restProps
  } = props;

  const { control } = useFormContext<any>();
  const { field, fieldState } = useController({
    name,
    control,
  });
  const context = React.useContext(FormContext);

  const shouldShowError = !!fieldState.error?.message;

  const _disabled = !context.editable || disabled;

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link,
      Superscript,
      SubScript,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder }),
    ],
    onUpdate(props) {
      const html = props.editor.getHTML();
      // remove tag <p> inside <li>
      // const modifiedHtml = html.replace(
      //   /(<li[^>]*>)(.*?)(<\/li[^>]*>)/g,
      //   (response) => {
      //     return response.match(/<\/?p[^>]*>/g)?.length === 2
      //       ? response.replace(/<\/?p[^>]*>/g, '')
      //       : response;
      //   },
      // );
      field.onChange(html);
    },
    editable: !_disabled,
    content: field.value,
  });

  const disabledControlProps = _disabled
    ? { interactive: false, active: false, disabled: true }
    : {};

  return (
    <CustomFormControl
      {...restProps}
      label={label}
      error={fieldState.error?.message}
      disabled={_disabled}
    >
      <RichTextEditor
        {...restProps}
        editor={editor}
        styles={{
          root: {
            ...(_disabled
              ? { ...styles.reset, cursor: 'not-allowed' }
              : {
                  border: `1px solid ${stylesColors.skyDefault}`,
                  borderRadius: '8px',
                }),
            ...(shouldShowError ? { border: '1px solid red' } : {}),
            ...overrides?.root,
          },
          toolbar: {
            ...(_disabled
              ? {
                  backgroundColor: '#EEEEEE',
                  borderBottomColor: '#AFAFAF',
                }
              : { borderRadius: '8px 8px 0 0' }),
            ...overrides?.toolbar,
          },
          control: {
            ...(_disabled
              ? {
                  backgroundColor: '#EEEEEE',
                  borderColor: '#AFAFAF',
                  cursor: 'not-allowed',
                }
              : {}),
            ...overrides?.control,
          },
          content: {
            minHeight: '76px',
            ...(_disabled
              ? {
                  borderBottomColor: '#AFAFAF',
                  backgroundColor: '#EEEEEE',
                }
              : { borderRadius: '8px' }),
            ...overrides?.content,
          },
          ...overrides,
        }}
      >
        {!_disabled && (
          <RichTextEditor.Toolbar>
            {controls.includes('default') && (
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Bold {...disabledControlProps} />
                <RichTextEditor.Italic {...disabledControlProps} />
                <RichTextEditor.Strikethrough {...disabledControlProps} />
                <RichTextEditor.Underline {...disabledControlProps} />
                <RichTextEditor.OrderedList {...disabledControlProps} />
                {controls.includes('bulletList') && (
                  <RichTextEditor.BulletList {...disabledControlProps} />
                )}
              </RichTextEditor.ControlsGroup>
            )}

            {controls.includes('textAlignment') && (
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.AlignLeft {...disabledControlProps} />
                <RichTextEditor.AlignCenter {...disabledControlProps} />
                <RichTextEditor.AlignRight {...disabledControlProps} />
                <RichTextEditor.AlignJustify {...disabledControlProps} />
              </RichTextEditor.ControlsGroup>
            )}

            {(controls.includes('subscript') ||
              controls.includes('superscript')) && (
              <RichTextEditor.ControlsGroup>
                {controls.includes('subscript') && (
                  <RichTextEditor.Subscript {...disabledControlProps} />
                )}
                {controls.includes('superscript') && (
                  <RichTextEditor.Superscript {...disabledControlProps} />
                )}
              </RichTextEditor.ControlsGroup>
            )}

            {controls.includes('heading') && (
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.H1 {...disabledControlProps} />
                <RichTextEditor.H2 {...disabledControlProps} />
                <RichTextEditor.H3 {...disabledControlProps} />
                <RichTextEditor.H4 {...disabledControlProps} />
                <RichTextEditor.H5 {...disabledControlProps} />
                <RichTextEditor.H6 {...disabledControlProps} />
              </RichTextEditor.ControlsGroup>
            )}

            {controls.includes('link') && (
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Link {...disabledControlProps} />
                <RichTextEditor.Unlink {...disabledControlProps} />
              </RichTextEditor.ControlsGroup>
            )}
          </RichTextEditor.Toolbar>
        )}

        <RichTextEditor.Content />
      </RichTextEditor>
    </CustomFormControl>
  );
}
